/**
 * MAISON MERCIER — POP-UP DE SORTIE (élément <mm-sortie>, rendu par sections/mm-sortie.liquid)
 * Source versionnée : seo-aeo-optimizer/src/retention/theme/assets/mm-sortie.js
 *
 * Règles (décision du 2026-10-03) :
 *  · ordinateur seulement : `(hover: hover) and (pointer: fine)` — jamais sur écran tactile ;
 *  · déclenché quand la souris quitte la fenêtre PAR LE HAUT (onglets, barre d'adresse) ;
 *  · jamais avant N secondes de présence sur la page ;
 *  · au plus une fois tous les N jours — la mémoire s'écrit à l'AFFICHAGE, donc toutes les
 *    fermetures se valent (croix, bouton, Échap, clic à côté). Le pop-up natif du thème
 *    (assets/popup-banner.js) ne mémorisait que la croix : il revenait à chaque sortie ;
 *  · jamais après le dernier jour de l'offre (heure de Bruxelles) ;
 *  · jamais par-dessus un tiroir ou une autre fenêtre déjà ouverte ;
 *  · jamais tant que le bandeau cookies attend une réponse (une fenêtre à la fois),
 *    ni si les cookies de « préférences » sont refusés (la mémoire de 7 jours en dépend) ;
 *  · aucune animation : le mouvement est réservé aux articles.
 * Mesure : événements Clarity `mm_sortie_affiche`, `mm_sortie_whatsapp`, `mm_sortie_panier`.
 *
 * MODE TEST : ajouter `?mm_sortie=test` (ou `&mm_sortie=test`) à l'adresse. La fenêtre s'ouvre
 * tout de suite, sur tout appareil, avec en bas la liste des conditions telles qu'elles valent
 * pour CE navigateur. Rien n'est mémorisé, rien n'est mesuré.
 */
(() => {
  if (customElements.get("mm-sortie")) return;

  const CLE = "mm_sortie_affiche_le";
  const JOUR_MS = 86400000;

  const lireMemoire = () => {
    try {
      return Number(window.localStorage.getItem(CLE)) || 0;
    } catch (e) {
      return 0;
    }
  };

  const ecrireMemoire = (t) => {
    try {
      window.localStorage.setItem(CLE, String(t));
    } catch (e) {
      /* stockage refusé : le pop-up reste limité à une fois par page */
    }
  };

  const jourBruxelles = () => {
    try {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Brussels",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  };

  const mesurer = (nom) => {
    try {
      if (typeof window.clarity === "function") window.clarity("event", nom);
    } catch (e) {
      /* la mesure ne doit jamais casser la page */
    }
  };

  class MmSortie extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      if (!this.dialog || typeof this.dialog.showModal !== "function") return;

      this.querySelectorAll("[data-mm-fermer]").forEach((bouton) => {
        bouton.addEventListener("click", () => this.fermer());
      });

      // Un clic sur le fond (hors du cadre de la fenêtre) ferme.
      this.dialog.addEventListener("click", (e) => {
        if (e.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        const dehors = e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
        if (dehors) this.fermer();
      });

      const whatsapp = this.querySelector("[data-mm-whatsapp] a");
      if (whatsapp) whatsapp.addEventListener("click", () => mesurer("mm_sortie_whatsapp"));
      const panier = this.querySelector("[data-mm-panier]");
      if (panier) panier.addEventListener("click", () => mesurer("mm_sortie_panier"));

      // Éditeur de thème : la fenêtre s'ouvre quand la section est sélectionnée.
      if (window.Shopify && window.Shopify.designMode) {
        const maSection = () => this.closest(".shopify-section");
        document.addEventListener("shopify:section:select", (e) => {
          if (maSection() && maSection().id === "shopify-section-" + e.detail.sectionId) this.ouvrir(true);
        });
        document.addEventListener("shopify:section:deselect", (e) => {
          if (maSection() && maSection().id === "shopify-section-" + e.detail.sectionId) this.fermer();
        });
        return;
      }

      if (new URLSearchParams(window.location.search).get("mm_sortie") === "test") {
        this.modeTest();
        return;
      }

      if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

      const dernierJour = this.dataset.dernierJour || "";
      if (dernierJour && jourBruxelles() > dernierJour) return;

      const jours = Math.max(1, Number(this.dataset.jours) || 7);
      if (Date.now() - lireMemoire() < jours * JOUR_MS) return;

      const delai = Math.max(0, Number(this.dataset.delai) || 0) * 1000;
      this.pretA = Date.now() + delai;

      // Les deux dernières hauteurs de la souris : une sortie RAPIDE vers les onglets peut être
      // signalée avec une hauteur encore positive ; la direction du mouvement la trahit.
      this.dernierY = null;
      this.avantDernierY = null;
      this.surMouvement = (e) => {
        this.avantDernierY = this.dernierY;
        this.dernierY = e.clientY;
      };
      document.addEventListener("mousemove", this.surMouvement, { passive: true });

      this.surSortie = (e) => {
        if (e.relatedTarget || e.toElement) return; // la souris passe d'un élément à un autre
        const parLeHaut = e.clientY <= 20
          || (this.dernierY !== null && this.avantDernierY !== null && this.dernierY <= 80 && this.dernierY < this.avantDernierY);
        if (!parLeHaut) return; // elle quitte la fenêtre par un côté ou par le bas
        if (Date.now() < this.pretA) return; // trop tôt
        if (this.autreFenetreOuverte()) return;
        if (!this.consentementOk()) return; // bandeau cookies en attente, ou préférences refusées
        this.ouvrir(false);
      };
      document.addEventListener("mouseout", this.surSortie);
    }

    disconnectedCallback() {
      if (this.surSortie) document.removeEventListener("mouseout", this.surSortie);
      if (this.surMouvement) document.removeEventListener("mousemove", this.surMouvement);
    }

    autreFenetreOuverte() {
      if (document.body.classList.contains("overflow-hidden")) return true;
      return Boolean(document.querySelector("dialog[open], modal-dialog[open], popup-banner[open], cart-drawer.active"));
    }

    /**
     * Le bandeau cookies passe AVANT : une seule fenêtre à la fois (contrat CRO §27).
     * Et sans accord « préférences », on ne peut pas mémoriser l'affichage : donc on n'affiche pas.
     */
    consentementOk() {
      const bandeau = document.getElementById("shopify-pc__banner");
      if (bandeau && bandeau.getClientRects().length > 0 && getComputedStyle(bandeau).visibility !== "hidden") return false;
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return true;
      try {
        // ⚠️ PAS `shouldShowBanner()` : il reste VRAI après la réponse (mesuré le 2026-10-03) —
        // il dit « région soumise au bandeau », pas « réponse en attente ».
        // `preferencesProcessingAllowed()` : faux tant que pas de réponse ou si refus ; vrai sinon.
        if (typeof cp.preferencesProcessingAllowed === "function" && !cp.preferencesProcessingAllowed()) return false;
      } catch (e) {
        return false;
      }
      return true;
    }

    /** Mode test : ouverture immédiate + diagnostic. L'API cookies se charge en différé : on l'attend 3 s. */
    async modeTest() {
      for (let i = 0; i < 15 && !(window.Shopify && window.Shopify.customerPrivacy); i++) {
        await new Promise((r) => setTimeout(r, 200));
      }
      const zone = this.querySelector("[data-mm-diagnostic]");
      if (zone) {
        zone.textContent = "";
        this.diagnostic().forEach((ligne) => {
          const li = document.createElement("li");
          li.textContent = ligne;
          zone.appendChild(li);
        });
        zone.hidden = false;
      }
      this.ouvrir(true);
    }

    diagnostic() {
      const lignes = ["MODE TEST — en vrai, la fenêtre s'ouvre seulement si tout est ✓ :"];
      const theme = (window.Shopify && window.Shopify.theme) || {};
      lignes.push(`Thème : ${theme.name || "?"} (${theme.role || "?"})`);
      const ordinateur = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      lignes.push(ordinateur ? "Appareil : ordinateur avec souris ✓" : "Appareil : écran tactile ✗ — rien ne s'affiche sur téléphone ou tablette");
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      let cookies = "API cookies absente — autorisé ✓";
      try {
        if (cp && typeof cp.preferencesProcessingAllowed === "function") {
          if (cp.preferencesProcessingAllowed()) cookies = "acceptés ✓";
          else {
            const c = typeof cp.currentVisitorConsent === "function" ? cp.currentVisitorConsent() : {};
            cookies = c && c.preferences === "no"
              ? "REFUSÉS ✗ — rien ne s'affichera dans ce navigateur"
              : "pas encore de réponse ✗ — répondre au bandeau";
          }
        }
      } catch (e) {
        cookies = "illisibles ✗";
      }
      lignes.push(`Cookies « préférences » : ${cookies}`);
      const vu = lireMemoire();
      const jours = Math.max(1, Number(this.dataset.jours) || 7);
      lignes.push(vu
        ? `Déjà affiché le ${new Date(vu).toLocaleString("fr-BE")} ✗ — de nouveau après le ${new Date(vu + jours * JOUR_MS).toLocaleDateString("fr-BE")}`
        : "Jamais affiché dans ce navigateur ✓");
      const dernierJour = this.dataset.dernierJour || "";
      lignes.push(`Offre valable jusqu'au ${dernierJour} ${dernierJour && jourBruxelles() > dernierJour ? "✗ terminée" : "✓"}`);
      lignes.push(`Déclencheur : ${Number(this.dataset.delai) || 0} s sur la page, puis la souris quitte la fenêtre PAR LE HAUT (vers les onglets)`);
      return lignes;
    }

    ouvrir(apercuEditeur) {
      if (this.dialog.open) return;
      if (this.surSortie) document.removeEventListener("mouseout", this.surSortie);
      if (this.surMouvement) document.removeEventListener("mousemove", this.surMouvement);
      if (!apercuEditeur) {
        ecrireMemoire(Date.now());
        mesurer("mm_sortie_affiche");
      }
      this.dialog.showModal();
      this.majPanier();
    }

    fermer() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    async majPanier() {
      const lien = this.querySelector("[data-mm-panier]");
      if (!lien) return;
      try {
        const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        const reponse = await fetch(racine + "cart.js", { headers: { Accept: "application/json" } });
        const panier = await reponse.json();
        if (panier && panier.item_count > 0) {
          lien.textContent = lien.dataset.libelle + " (" + panier.item_count + ")";
          lien.hidden = false;
        }
      } catch (e) {
        /* panier illisible : le lien reste caché */
      }
    }
  }

  customElements.define("mm-sortie", MmSortie);
})();
